/**
 * アセットパス定義の集約モジュール（アセット要件の受け入れ基準）。
 * 教材データ・プレゼンテーション層からは論理名（例: `noma/smile`, `dojo-1`）のみで参照し、
 * 実ファイルパス（拡張子・配置ディレクトリ）への解決はこのモジュールが一手に担う。
 * 実ファイル名・拡張子の変更が教材データの書き換えを引き起こさない。
 *
 * 背景は JPEG。立ち絵・朱印・アイコン・ロゴはクロマキー抜きの PNG。
 * 免状帳の台紙は JPEG。音源はまだプレースホルダの `.wav`。
 */

/** 師範代ノーマの表情差分（立ち絵のクロスフェード用）。 */
export const COMPANION_EXPRESSIONS = [
  "neutral",
  "smile",
  "surprised",
  "worried",
  "smug",
  "cheer",
] as const;
export type CompanionExpression = (typeof COMPANION_EXPRESSIONS)[number];

/** 背景 9点（道場の稽古場7段 + 門 + 床の間）。 */
export const BACKGROUND_NAMES = [
  "dojo-1",
  "dojo-2",
  "dojo-3",
  "dojo-4",
  "dojo-5",
  "dojo-6",
  "dojo-7",
  "gate",
  "tokonoma",
] as const;
export type BackgroundName = (typeof BACKGROUND_NAMES)[number];

/** 囃子（BGM）4種。 */
export const BGM_NAMES = ["gate", "hanashi", "keiko", "shiken"] as const;
export type BgmName = (typeof BGM_NAMES)[number];

/** 打ち音（SE）5種。 */
export const SE_NAMES = ["pass", "seal", "key", "deny", "page"] as const;
export type SeName = (typeof SE_NAMES)[number];

/** 師範代ボイスの章種別（ADV）。 */
export type VoiceChapterKind = "intro" | "outro";

/** 家紋朱印8種（七段 + 免許皆伝）。 */
export const STAMP_NAMES = [
  "level-1",
  "level-2",
  "level-3",
  "level-4",
  "level-5",
  "level-6",
  "level-7",
  "grand",
] as const;
export type StampName = (typeof STAMP_NAMES)[number];

/** 師範代ノーマの立ち絵（表情差分）のパスを解決する。 */
export function companionAssetPath(expression: CompanionExpression): string {
  return `/assets/characters/noma/${expression}.png`;
}

/** 背景画像のパスを解決する。 */
export function backgroundAssetPath(name: BackgroundName): string {
  return `/assets/backgrounds/${name}.jpg`;
}

/** 囃子（BGM）のパスを解決する。プレースホルダ段階は `.wav`（実物差し替え時は `.mp3` を想定）。 */
export function bgmAssetPath(name: BgmName): string {
  return `/assets/audio/bgm/${name}.wav`;
}

/** 打ち音（SE）のパスを解決する。プレースホルダ段階は `.wav`（実物差し替え時は `.mp3` を想定）。 */
export function seAssetPath(name: SeName): string {
  return `/assets/audio/se/${name}.wav`;
}

/**
 * 師範代ノーマの ADV セリフ音声（事前生成 MP3）のパス。
 * `scripts/generate-noma-voice.py` の出力規約と一致させること。
 */
export function advVoiceAssetPath(
  levelId: string,
  kind: VoiceChapterKind,
  sceneIndex: number,
  lineIndex: number,
): string {
  return `/assets/audio/voice/${levelId}/${kind}/s${sceneIndex}-l${lineIndex}.mp3`;
}

/**
 * 師範代ノーマの稽古中声かけ音声（事前生成 MP3）のパス。
 * `occurrence` は当該トリガー候補の並び位置（0始まり）。
 */
export function reactionVoiceAssetPath(
  levelId: string,
  trigger: string,
  occurrence: number,
  lineIndex = 0,
): string {
  return `/assets/audio/voice/${levelId}/reaction/${trigger}-${occurrence}-l${lineIndex}.mp3`;
}

/** 家紋朱印のパスを解決する。 */
export function stampAssetPath(name: StampName): string {
  return `/assets/stamps/${name}.png`;
}

/** ロゴ（円相）のパスを解決する。 */
export function logoAssetPath(): string {
  return "/assets/ui/logo.png";
}

/** 免状帳の台紙テクスチャのパスを解決する。 */
export function menjoAssetPath(): string {
  return "/assets/ui/menjo.jpg";
}

/** アイコン・家紋・円相の生成 PNG パスを解決する。 */
export function iconAssetPath(name: string): string {
  if (name === "enso") {
    return logoAssetPath();
  }
  if (name === "mon-blank") {
    return "/assets/stamps/blank.png";
  }
  if (name === "mon-master") {
    return stampAssetPath("grand");
  }
  if (name.startsWith("mon-")) {
    return stampAssetPath(`level-${name.slice(4)}` as StampName);
  }
  return `/assets/icons/${name}.png`;
}
